import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    collection: 'iconiques',
    image_url: '',
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/news`);
      setNews(data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingNews) {
        await axios.put(`${API}/news/${editingNews.id}`, formData, { withCredentials: true });
        toast.success('Nouveauté mise à jour');
      } else {
        await axios.post(`${API}/news`, formData, { withCredentials: true });
        toast.success('Nouveauté ajoutée');
      }
      fetchNews();
      resetForm();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette nouveauté ?')) return;
    try {
      await axios.delete(`${API}/news/${id}`, { withCredentials: true });
      toast.success('Nouveauté supprimée');
      fetchNews();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', collection: 'iconiques', image_url: '' });
    setEditingNews(null);
    setShowModal(false);
  };

  const openEdit = (item) => {
    setFormData(item);
    setEditingNews(item);
    setShowModal(true);
  };

  return (
    <AdminLayout title="Gestion des Nouveautés">
      <button
        onClick={() => setShowModal(true)}
        className="btn-primary px-6 py-3 rounded-full mb-6 inline-flex items-center gap-2"
        data-testid="add-news-btn"
      >
        <Plus size={20} />
        Ajouter une nouveauté
      </button>

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <div className="grid gap-4">
          {news.map((item) => (
            <div key={item.id} className="bg-white p-4 rounded-lg border flex justify-between items-start" data-testid={`news-item-${item.id}`}>
              <div>
                <h3 className="text-lg font-medium text-[#5C2018]">{item.title}</h3>
                <p className="text-sm text-[#332724]">{item.description}</p>
                <span className="text-xs text-[#B8922A]">{item.collection}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(item)} className="p-2 text-[#1E7A6A] hover:bg-[#1E7A6A]/10 rounded" data-testid={`edit-news-${item.id}`}>
                  <Edit2 size={18} />
                </button>
                <button onClick={() => handleDelete(item.id)} className="p-2 text-red-600 hover:bg-red-50 rounded" data-testid={`delete-news-${item.id}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={resetForm}>
          <div className="bg-white p-8 rounded-lg max-w-2xl w-full" onClick={(e) => e.stopPropagation()} data-testid="news-modal">
            <h2 className="text-2xl mb-4">{editingNews ? 'Modifier' : 'Ajouter'} une nouveauté</h2>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="Titre"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full p-2 border rounded mb-3"
                required
                data-testid="news-title-input"
              />
              <textarea
                placeholder="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border rounded mb-3"
                rows="3"
                required
              />
              <select
                value={formData.collection}
                onChange={(e) => setFormData({ ...formData, collection: e.target.value })}
                className="w-full p-2 border rounded mb-3"
              >
                <option value="iconiques">Les Iconiques</option>
                <option value="pepites">Les Pépites</option>
                <option value="audacieux">Les Audacieux</option>
              </select>
              <input
                type="url"
                placeholder="URL de l'image"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full p-2 border rounded mb-3"
              />
              <div className="flex gap-3">
                <button type="submit" className="btn-primary px-6 py-2 rounded-full" data-testid="news-submit">
                  Enregistrer
                </button>
                <button type="button" onClick={resetForm} className="btn-secondary px-6 py-2 rounded-full">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminNews;
